import React, { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';
import { setAnalyticsConsent } from '../hooks/useAnalytics';

const CONSENT_KEY = 'siq_analytics_consent';

/**
 * GDPR-style cookie consent banner.  Shown only on first visit (no consent
 * stored yet).  Click "Accept" → analytics tracking starts on the next route.
 * Click "Decline" → tracker becomes a no-op for this device.
 *
 * Note: tracker hooks read consent at the moment they fire, so a fresh
 * "Accept" tick will start tracking the very next page-view.
 */
export default function AnalyticsConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const choice = localStorage.getItem(CONSENT_KEY);
    if (!choice) setVisible(true);
  }, []);

  if (!visible) return null;

  const accept = () => {
    setAnalyticsConsent(true);
    setVisible(false);
    // Reload so the analytics hooks start tracking from a fresh state including
    // the original referrer / UTM params (they fire on mount).
    window.location.reload();
  };
  const decline = () => {
    setAnalyticsConsent(false);
    setVisible(false);
  };

  return (
    <div
      data-testid="analytics-consent-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-6 sm:max-w-sm z-[60] bg-white rounded-xl shadow-2xl border border-[#c3c6d7]/40 p-4 flex items-start gap-3"
      style={{ fontFamily: "'Inter', sans-serif" }}>
      <Cookie className="w-5 h-5 text-[#004ac6] shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-bold text-[#0b1c30] tracking-tight mb-1">Analytics cookies</div>
        <p className="text-[11px] text-[#434655] leading-relaxed mb-3">
          We use a single cookie to measure how Sovereign IQ is being used (page views, scroll depth, where you came from).
          No third-party trackers, no personal data sold. You can decline and the site works exactly the same.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={accept}
            data-testid="analytics-consent-accept"
            className="px-3 py-1.5 rounded-md text-[11px] font-bold text-white bg-[#004ac6] hover:bg-[#003a9e] transition-all">
            Accept
          </button>
          <button
            onClick={decline}
            data-testid="analytics-consent-decline"
            className="px-3 py-1.5 rounded-md text-[11px] font-bold text-[#434655] hover:text-[#0b1c30] hover:bg-[#eff4ff] transition-all">
            Decline
          </button>
        </div>
      </div>
      <button
        onClick={decline}
        aria-label="Close"
        className="p-1 rounded text-[#737686] hover:bg-[#eff4ff] shrink-0">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
